import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { RepCourseDetail } from '../../../models/practice-course.types';
import { PracticeCoursesService } from '../../../services/practice-courses.service';

@Component({
  selector: 'app-practice-course-rep',
  imports: [CommonModule, RouterModule, ButtonModule, CardModule, TableModule, TagModule],
  templateUrl: './practice-course-rep.component.html',
  styleUrl: '../practice-courses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeCourseRepComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(PracticeCoursesService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly courseVersionId = this.route.snapshot.paramMap.get('courseVersionId')!;
  protected detail = signal<RepCourseDetail | null>(null);
  protected loading = signal(true);
  protected error = signal<string | null>(null);

  private readonly nameMap = computed(() => {
    const map = new Map<string, string>();
    for (const attempt of this.detail()?.attempts ?? []) {
      if (attempt.moduleId) map.set(attempt.moduleId, attempt.moduleName);
      if (attempt.scenarioRangeId) map.set(attempt.scenarioRangeId, attempt.scenarioRangeName);
    }
    return map;
  });

  /** `load()` is bound to "Try again"; subscribing inside it would start a concurrent request per
   * click and let an older response overwrite a newer one. One Subject with `switchMap` cancels the
   * in-flight request instead, and `catchError` sits inside the switchMap so a failure resolves the
   * inner stream rather than terminating the Subject and killing every later retry. */
  private readonly loadRequest$ = new Subject<number>();

  constructor() {
    this.loadRequest$
      .pipe(
        switchMap((userId) =>
          this.service.getRep(this.courseVersionId, userId).pipe(
            catchError((error) => {
              this.error.set(error.message);
              this.loading.set(false);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (detail) => {
          if (!detail) return;
          this.detail.set(detail);
          this.loading.set(false);
        },
      });
    this.load();
  }

  protected load() {
    this.loading.set(true);
    this.error.set(null);
    this.loadRequest$.next(Number(this.route.snapshot.paramMap.get('userId')));
  }

  protected percent(value: number | null | undefined) {
    return Math.round((value ?? 0) * 100);
  }

  protected moduleName(moduleId: string | null, orderIndex: number) {
    return this.nameMap().get(moduleId ?? '') ?? `Module ${orderIndex + 1}`;
  }

  protected scenarioName(scenarioRangeId: string) {
    return this.nameMap().get(scenarioRangeId) ?? `${scenarioRangeId.slice(0, 8)}…`;
  }

  protected severity(
    value: string | boolean | null,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (
      value === true ||
      ['CERTIFIED', 'PASSED', 'ACTIVE', 'READY_FOR_LIVE_CALLS'].includes(String(value))
    )
      return 'success';
    if (value === false || ['BLOCKED', 'FAILED', 'CANCELED'].includes(String(value)))
      return 'danger';
    if (value === null) return 'secondary';
    return 'info';
  }

  protected label(value: string) {
    return value
      .replaceAll('_', ' ')
      .toLowerCase()
      .replace(/^./, (char) => char.toUpperCase());
  }
}
